/**
 * Copyright 2025 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */

import {scaleBand, scalePow} from 'd3-scale';
import {select} from 'd3-selection';

class AudioVolumeVisualization {
  startRender(audioVolumeMeter, visualizationType, options) {
    this.stopped = false;

    return startRender.call(this, audioVolumeMeter, visualizationType, options);
  }

  stopRender() {
    this.stopped = true;
  }
}

const defaultMargin = {
  top: 0,
  left: 0,
  bottom: 0,
  right: 0
};

function startRender(audioVolumeMeter, visualizationType, options) {
  options.margin = Object.assign(options.margin || {}, defaultMargin);
  options.padding = options.padding || 0;

  switch (visualizationType) {
    case 'histogram':
      return;
    case 'gain':
      return startRenderGain.call(this, audioVolumeMeter, options);
    default:
      throw new Error('Visualization type not supported');
  }
}

function startRenderGain(audioVolumeMeter, options) {
  const that = this;
  const element = select(options.element).node();
  const margin = {
    top: options.margin.top,
    left: options.margin.left,
    bottom: options.margin.bottom,
    right: options.margin.right
  };
  let width = element.getBoundingClientRect().width - margin.left - margin.right;
  let height = element.getBoundingClientRect().height - margin.top - margin.bottom;
  const metrics = options.metrics || ['value'];
  const scale = {
    x: scaleBand()
      .range([0, width])
      .domain(metrics)
      .padding(options.padding),
    y: scalePow()
      .exponent(0.25)
      .domain([1, 0])
      .range([0, height])
  };
  const svg = select(options.element)
    .append('svg')
    .append('g')
    .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
  let lastValue = {
    date: Date.now(),
    value: 0.,
    smoothedValue: 0.,
    smoothedPeakValue: 0.,
    capped: 0
  };

  audioVolumeMeter.onValue((update) => {
    lastValue = update;
  });

  const shouldResize = () => {
    const newWidth = element.getBoundingClientRect().width - margin.left - margin.right;
    const newHeight = element.getBoundingClientRect().height - margin.top - margin.bottom;

    return width !== newWidth || height !== newHeight;
  };

  const resize = () => {
    width = element.getBoundingClientRect().width - margin.left - margin.right;
    height = element.getBoundingClientRect().height - margin.top - margin.bottom;

    scale.x.range([0, width]);
    scale.y.range([0, height]);
  };

  const renderStep = () => {
    if (that.stopped) {
      return;
    }

    if (shouldResize()) {
      resize();
    }

    const textColorClass = options.textColors[getAudioState(lastValue)];
    const barColorClass = options.barColor;
    const data = metrics.map((metric) => {
      return lastValue[metric];
    });

    svg.selectAll('*').remove();
    svg.attr('class', textColorClass);
    svg.selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', barColorClass)
      .attr('x', (d, i) => {
        return scale.x(metrics[i]);
      })
      .attr('width', scale.x.bandwidth())
      .attr('y', scale.y)
      .attr('height', (d) => {
        return scale.y(0) - scale.y(d);
      });
    svg.append('text')
      .attr('x', scale.x.bandwidth() / 2)
      .attr('y', scale.y(0) / 2)
      .attr('dy', 0.7 * scale.y(0) / 4)
      .attr('font-size', scale.y(0) / 2)
      .attr('class', textColorClass)
      .style('text-anchor', 'middle')
      .style('text-align', 'center')
      .text(options.title);

    setTimeout(() => {
      window.requestAnimationFrame(renderStep);
    }, 1000 / 24);
  };

  renderStep();

  window.addEventListener('resize', resize);
}

function getAudioState(audio) {
  let state = 'silence';

  if (audio.clipped > 0) {
    state = 'clipped';
  } else if (audio.smoothedPeakValue > 0) {
    state = audio.value > audio.smoothedValue ? 'high' : 'low';
  }

  return state;
}

export default AudioVolumeVisualization;